import { inject, Injectable } from '@angular/core';
import { injectMutation, injectQuery, QueryClient } from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';

import { QUERY_KEYS } from '@core/constans';
import { ApiResponse } from '@core/types';
import { PaginationConfig, PaginationResult } from '@core/services/unit.service';
import { AlliedService, AlliedServiceCreate, AlliedServiceUpdate } from '@core/types/allied-service.type';
import { AlliedServiceService } from '@core/services';
import { environment } from '@environments/environment';

@Injectable({ providedIn: 'root' })
export class AlliedServiceQuery {
  private readonly service = inject(AlliedServiceService);
  private readonly queryClient = inject(QueryClient);

  useAlliedServicesGrid(config: () => PaginationConfig) {
    return injectQuery(() => {
      const c = config();
      return {
        queryKey: QUERY_KEYS.alliedServices.grid(c),
        queryFn: () => lastValueFrom(this.service.grid(c)),
        select: (res: ApiResponse<PaginationResult<AlliedService>>) => res.data,
        staleTime: environment.staleTime,
      };
    });
  }

  useAlliedServiceDetail(id: () => string | null) {
    return injectQuery(() => {
      const v = id();
      return {
        queryKey: QUERY_KEYS.alliedServices.detail(v || ''),
        queryFn: () => lastValueFrom(this.service.get(v!)),
        select: (res: ApiResponse<AlliedService>) => res.data,
        enabled: !!v,
      };
    });
  }

  useCreateAlliedService() {
    return injectMutation(() => ({
      mutationFn: (data: AlliedServiceCreate) => lastValueFrom(this.service.create(data)),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.alliedServices.all }),
    }));
  }

  useUpdateAlliedService() {
    return injectMutation(() => ({
      mutationFn: (data: AlliedServiceUpdate) => lastValueFrom(this.service.update(data)),
      onSuccess: (_res, variables) => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.alliedServices.detail(variables.id) });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.alliedServices.all });
      },
    }));
  }

  useDeleteAlliedService() {
    return injectMutation(() => ({
      mutationFn: (id: string) => lastValueFrom(this.service.delete(id)),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.alliedServices.all }),
    }));
  }
}

